// Photo Moderation (Phase 3 Module 15 / Slice B) — parent + admin helpers.
//
// Parents call `reportChildPhoto` from the lightbox. Admins use
// `listPhotoModerationQueue`, `moderateChildPhoto`, and
// `getPhotoModerationQueueCount` from /admin/photos/moderation.
//
// All cross-user visibility is enforced by the RPCs themselves
// (SECURITY DEFINER with role/org guards). The thin wrappers here
// just keep call sites tidy.

import { supabase, isSupabaseConfigured } from "./supabase";

function assertLive() {
  if (!isSupabaseConfigured) {
    throw new Error("Photo moderation requires live Supabase.");
  }
}

// ---------------------------------------------------------------------------
// Parent — report
// ---------------------------------------------------------------------------

/**
 * Flag a child photo for admin review. Only the child's family can call;
 * the RPC rejects otherwise. `reason` is required and capped at 280 chars.
 *
 * After a successful report we fire-and-forget a POST to
 * /api/notifications/send/photo-reported so admins see the flag in their
 * Notification Center + push inbox within seconds rather than on their
 * next dashboard visit. The notify call never blocks the UI — if it
 * fails (e.g., the admin directory lookup 500s), the report itself is
 * already persisted in the DB and the dashboard queue still shows it.
 */
export async function reportChildPhoto(photoId, reason) {
  assertLive();
  const { data, error } = await supabase.rpc("rpc_report_child_photo", {
    p_photo_id: photoId,
    p_reason: (reason || "").trim(),
  });
  if (error) throw error;

  // Fire-and-forget admin fan-out. Errors are swallowed — the parent
  // should not see this as a report failure.
  try {
    notifyAdminsOfPhotoReport(data).catch(() => { /* silent */ });
  } catch {
    /* silent */
  }

  return data;
}

/**
 * Posts the reported photo ID to /api/notifications/send/photo-reported.
 * The backend verifies the caller's report and hydrates all alert context
 * from canonical data, so client-supplied organization/person/reason values
 * can never redirect a notification.
 */
async function notifyAdminsOfPhotoReport(photo) {
  if (!photo?.id) return;
  const { data: session } = await supabase.auth.getSession();
  const token = session?.session?.access_token;
  if (!token) return;

  const base = process.env.REACT_APP_BACKEND_URL;
  if (!base) return;
  await fetch(`${base}/api/notifications/send/photo-reported`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      photo_id: photo.id,
    }),
  });
}

// Canonical list of reasons surfaced in the parent-facing picker. We keep
// it short so the flag sheet stays mobile-friendly, and track a free-form
// "other" fallback for anything the fixed list doesn't cover.
export const REPORT_REASONS = [
  { key: "wrong_child",    label: "Wrong child in photo" },
  { key: "privacy",        label: "Privacy concern" },
  { key: "unflattering",   label: "Unflattering / please remove" },
  { key: "other",          label: "Something else" },
];

// ---------------------------------------------------------------------------
// Admin — queue
// ---------------------------------------------------------------------------

/**
 * Returns the flagged-photo count for the admin dashboard KPI.
 *
 * Resilient by design: pre-migration or for a non-admin caller, the RPC
 * either doesn't exist (PGRST202) or is a no-op; either way we return 0
 * rather than throw, so the dashboard card shows a stable number instead
 * of an indefinite "…". Admins still get the real count once the
 * migration lands.
 */
export async function getPhotoModerationQueueCount() {
  if (!isSupabaseConfigured) return 0;
  const { data, error } = await supabase.rpc("rpc_photo_moderation_queue_count");
  if (error) {
    // Resilient defensive default — non-admin callers, missing migration,
    // or transient network races all land here. We swallow silently so
    // the dashboard KPI just shows 0 instead of polluting the production
    // console. Inspect via DevTools "Verbose" level if you need to debug.
    // eslint-disable-next-line no-console
    console.debug("[photoModeration] queue-count fallback to 0:", error.message);
    return 0;
  }
  return Number(data) || 0;
}

/**
 * List flagged (default) or any-status moderation rows with enriched
 * metadata (child / classroom / uploader / reporter names). Admin-only
 * via SECURITY DEFINER.
 */
export async function listPhotoModerationQueue({ status = "flagged", limit = 50 } = {}) {
  assertLive();
  const { data, error } = await supabase.rpc("rpc_list_photo_moderation_queue", {
    p_status: status,
    p_limit: limit,
  });
  if (error) throw error;
  return data || [];
}

/**
 * Approve / reject a flagged photo. `note` is optional. */
export async function moderateChildPhoto(photoId, action, note = null) {
  assertLive();
  if (!["approve", "reject"].includes(action)) {
    throw new Error(`Invalid moderation action: ${action}`);
  }
  const { data, error } = await supabase.rpc("rpc_moderate_child_photo", {
    p_photo_id: photoId,
    p_action: action,
    p_note: note?.trim() || null,
  });
  if (error) throw error;
  return data;
}

/**
 * Full moderation timeline for one photo (admin-only). Returns events
 * oldest -> newest so the UI can render top-down without sorting.
 * Resilient: pre-migration or for a non-admin caller, returns []
 * instead of throwing so the card's "Show history" expand falls back
 * to a terse empty state.
 */
export async function listPhotoModerationHistory(photoId) {
  if (!isSupabaseConfigured || !photoId) return [];
  const { data, error } = await supabase.rpc("rpc_list_photo_moderation_history", {
    p_photo_id: photoId,
  });
  if (error) {
    // Defensive default; surface only at debug level.
    // eslint-disable-next-line no-console
    console.debug("[photoModeration] history fallback to []:", error.message);
    return [];
  }
  return data || [];
}

/** Action labels + tones shared between the card header and the history row. */
export const MODERATION_ACTION_META = {
  reported: { label: "Reported",  tone: "text-[#C6851E]" },
  approved: { label: "Approved",  tone: "text-primary-hover" },
  rejected: { label: "Removed",   tone: "text-neutral-600" },
};

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

export const MODERATION_STATUSES = [
  { key: "pending",  label: "Awaiting review", tone: "border-amber-300 bg-amber-50 text-amber-800" },
  { key: "flagged",  label: "Reported",        tone: "border-[#C6851E]/40 bg-[#C6851E]/10 text-[#C6851E]" },
  { key: "approved", label: "Approved",        tone: "border-primary/30 bg-primary/10 text-primary-hover" },
  { key: "rejected", label: "Removed",         tone: "border-neutral-300 bg-neutral-100 text-neutral-600" },
];

export function moderationStatusMeta(key) {
  return MODERATION_STATUSES.find((s) => s.key === key) || MODERATION_STATUSES[0];
}

// Admin queue renders thumbnails — this wraps the bucket's createSignedUrl
// with the same 1h TTL helper the parent lightbox already uses, sharing
// the in-memory cache via lib/photos.js.
export { photoSignedUrl as moderationSignedUrl } from "./photos";

// ---------------------------------------------------------------------------
// Weekly moderation digest — admin-only summary of the past N days.
// Reads `child_photo_moderation_events` directly (admin RLS already
// allows SELECT, scoped to the caller's org) and pairs that with the
// current pending count from `rpc_photo_moderation_queue_count`. No new
// RPC required — keeps the deployment surface zero-migration.
// ---------------------------------------------------------------------------

export async function loadModerationDigest({ days = 7 } = {}) {
  assertLive();
  const since = new Date();
  since.setUTCDate(since.getUTCDate() - days);
  const sinceIso = since.toISOString();

  const [{ data: events, error: evErr }, pendingNow] = await Promise.all([
    supabase
      .from("child_photo_moderation_events")
      .select("id, action, created_at")
      .gte("created_at", sinceIso),
    getPhotoModerationQueueCount(),
  ]);
  if (evErr) throw evErr;
  const counts = { reported: 0, approved: 0, rejected: 0 };
  for (const e of events || []) {
    if (e.action === "reported") counts.reported += 1;
    else if (e.action === "approved") counts.approved += 1;
    else if (e.action === "rejected") counts.rejected += 1;
  }
  return {
    since: sinceIso,
    days,
    pending_now: pendingNow,
    ...counts,
    total_events: (events || []).length,
  };
}
